import { useNavigate, useSearchParams } from 'react-router-dom'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  useCreatePassword,
  useUpdatePassword,
  useLogout,
} from '@/api/user-manager'
import { routes } from '@/router'
import { Box, Stack, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { PasswordStrength } from '@/components'
import { PasswordFormData, passwordFormSchema } from './form.shema'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from '@/helpers'
import { useEffect } from 'react'

export function CreatePasswordPage({
  isRecovery = false,
}: {
  isRecovery?: boolean
}) {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { mutateAsync: logout } = useLogout()
  const queryClient = useQueryClient()

  const token = searchParams.get('token')

  const { handleSubmit, control } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordFormSchema),
    defaultValues: {
      password: '',
      confirm_password: '',
    },
  })

  const { mutateAsync: updatePassword, isPending: isUpdatingPassword } =
    useUpdatePassword({
      onSuccess: async () => {
        toast.success('Senha atualizada com sucesso!')
        setTimeout(() => logout(), 2000)
      },
    })

  const { mutateAsync: createPassword, isPending: isCreatingPassword } =
    useCreatePassword({
      onSuccess: async () => {
        await queryClient.refetchQueries({ queryKey: ['user-profile'] })
        navigate(routes.home)
      },
    })

  async function handleCreatePassword({ password }: PasswordFormData) {
    if (isRecovery) {
      await updatePassword({ new_password: password })
    } else {
      await createPassword({ password })
    }
  }

  useEffect(() => {
    if (!token) return

    localStorage.setItem('access_token', token)
    setSearchParams()
  }, [token, setSearchParams])

  return (
    <Box display="flex" justifyContent="center" alignItems="center">
      <Box
        component="form"
        onSubmit={handleSubmit(handleCreatePassword)}
        width={360}
      >
        <Typography variant="h3" align="center" mb={3}>
          {isRecovery ? 'Cadastrar nova senha' : 'Crie uma senha'}
        </Typography>

        <Stack spacing={3}>
          <Controller
            name="password"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                type="password"
                label="Senha"
                error={Boolean(error)}
                helperText={<PasswordStrength password={field.value} />}
                {...field}
              />
            )}
          />

          <Controller
            name="confirm_password"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                type="password"
                label="Confirme a sua senha"
                error={Boolean(error)}
                helperText={error?.message}
                {...field}
              />
            )}
          />

          <LoadingButton
            type="submit"
            size="large"
            fullWidth
            loading={isCreatingPassword || isUpdatingPassword}
          >
            {isRecovery ? 'Criar nova senha' : 'Criar senha'}
          </LoadingButton>
        </Stack>
      </Box>
    </Box>
  )
}
